import { ChangeDetectionStrategy, Component, OnInit, computed, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AcquisitionStoreService } from '../../../services/acquisition/acquisition.store.service';
import { GrowthStoreService, growthPercent, growthTone } from '../../../services/growth/growth.store.service';
import { OverviewStoreService } from '../../../services/overview/overview.store.service';
import { UserEngagedStoreService } from '../../../services/user/user-engaged.store.service';

/** The windows the engaged list can be computed over, in days. */
const PERIODS = [30, 60, 90, 120];

/**
 * Who actually does something, against the background of who merely exists.
 *
 * The page holds the base counters and the list of members who created or
 * joined at least one activity over a chosen window, ranked by how much they
 * did. The counters link to the screens that explain them.
 */
@Component({
  selector: 'bo-user-activity',
  templateUrl: './user-activity.component.html',
  styleUrls: ['./user-activity.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, TranslateModule, DatePipe],
})
export class UserActivityComponent implements OnInit {
  #overviewStore = inject(OverviewStoreService);
  #engagedStore = inject(UserEngagedStoreService);
  #acquisitionStore = inject(AcquisitionStoreService);
  #growthStore = inject(GrowthStoreService);

  readonly countFutureEvents = computed(() => this.#overviewStore.kpi().totalUpcomingActivities);
  readonly countPastEvents = computed(() => this.#overviewStore.kpi().totalPastActivities);
  readonly countUsers = computed(() => this.#overviewStore.kpi().totalUsers);
  readonly countGroups = computed(() => this.#overviewStore.kpi().totalGroups);
  readonly failed = this.#overviewStore.failed;

  readonly countEngaged = this.#engagedStore.countUsers;
  readonly engagedUsers = this.#engagedStore.engagedUsers;
  readonly lastDays = this.#engagedStore.lastDays;
  readonly periods = PERIODS;

  /** Share of the base that acted over the window: the page's one verdict. */
  readonly engagedShare = computed(() => {
    const total = this.countUsers();
    return total > 0 ? Math.round((this.countEngaged() / total) * 1000) / 10 : 0;
  });

  /** The complement, said out loud on the base card: how many exist and do nothing. */
  readonly inactiveShare = computed(() => Math.round((100 - this.engagedShare()) * 10) / 10);

  /** Average activities per active member over the window. */
  readonly averagePerActive = computed(() => {
    const count = this.countEngaged();
    if (count === 0) {
      return 0;
    }
    const total = this.engagedUsers().reduce((sum, user) => sum + user.totalEvents, 0);
    return Math.round((total / count) * 10) / 10;
  });

  /** Upcoming and past shares of everything ever created. */
  readonly upcomingShare = computed(() => {
    const total = this.countFutureEvents() + this.countPastEvents();
    return total > 0 ? Math.round((this.countFutureEvents() / total) * 1000) / 10 : 0;
  });

  readonly pastShare = computed(() => {
    const total = this.countFutureEvents() + this.countPastEvents();
    return total > 0 ? Math.round((this.countPastEvents() / total) * 1000) / 10 : 0;
  });

  /**
   * Growth of the active count against the window just before this one,
   * in percent. Null while the previous window is empty: a growth figure
   * against nothing would read as +Infinity.
   */
  readonly activesGrowth = computed(() => {
    const previous = this.#engagedStore.previousCount();
    if (previous === 0) {
      return null;
    }
    const value = Math.round(((this.countEngaged() - previous) / previous) * 1000) / 10;
    return { value, tone: growthTone(value) };
  });

  /**
   * Growth of monthly sign-ups, last full month against the one before.
   * The running month is excluded: half a month always loses to a full one.
   */
  readonly signupsGrowth = computed(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const full = this.#acquisitionStore
      .data()
      .cohorts.filter((cohort) => cohort.month < currentMonth)
      .sort((a, b) => a.month.localeCompare(b.month));
    if (full.length < 2) {
      return null;
    }
    const previous = full[full.length - 2].signups;
    if (previous === 0) {
      return null;
    }
    const last = full[full.length - 1].signups;
    const value = Math.round(((last - previous) / previous) * 1000) / 10;
    return { value, tone: growthTone(value) };
  });

  /** Month-over-month growth for the three stock tiles, from the growth endpoint. */
  private trendOf(pick: 'activitiesCreated' | 'activitiesHeld' | 'groupsCreated') {
    if (!this.#growthStore.available()) {
      return null;
    }
    const value = growthPercent(this.#growthStore.data()[pick]);
    return value === null ? null : { value, tone: growthTone(value) };
  }

  readonly creationsTrend = computed(() => this.trendOf('activitiesCreated'));
  readonly heldTrend = computed(() => this.trendOf('activitiesHeld'));
  readonly groupsTrend = computed(() => this.trendOf('groupsCreated'));

  /** How much actually happens per group, on average. */
  readonly activitiesPerGroup = computed(() => {
    const groups = this.countGroups();
    if (groups === 0) {
      return 0;
    }
    return Math.round(((this.countFutureEvents() + this.countPastEvents()) / groups) * 10) / 10;
  });

  /** The busiest member sets the scale every activity bar is drawn against. */
  readonly largestCount = computed(() =>
    Math.max(...this.engagedUsers().map((user) => user.totalEvents), 1),
  );

  ngOnInit(): void {
    this.#overviewStore.loadOverviewKpi();
    this.#acquisitionStore.load();
    this.#growthStore.load();
  }

  setPeriod(days: number): void {
    this.#engagedStore.getEngagedUsers(days);
  }

  barWidth(totalEvents: number): string {
    return `${Math.max((totalEvents / this.largestCount()) * 100, 4)}%`;
  }
}
